/**
 * LPA Schedule Instances API
 *
 * CRUD operations for `core.mod_lpa_schedule_instances`.
 *
 * Instances are materialized audit period rows. Each row represents a concrete
 * "you need to do this audit on this date" entry, frozen at generation time.
 *
 * For Phase 3, generation is manual (one instance at a time per schedule).
 * Automatic batch generation (pg_cron) will be added later.
 *
 * Key concepts:
 * - `frozen_at` is set at generation time → schedule changes don't affect it
 * - Unique constraint: (schedule_id, auditor_id, period_start)
 * - Status lifecycle: pending → in_progress → completed | overdue | skipped | cancelled
 * - Auditor substitution: reassign a single instance without touching the schedule
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  LpaScheduleInstance,
  LpaScheduleInstanceWithDetails,
  LpaScheduleFrequency,
  LpaScheduleQuestionExecution,
  GenerateInstanceInput,
  UpdateInstanceStatusInput,
  UpdateInstanceAuditorInput,
} from '../types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolves auditor profiles for a set of instance auditor IDs.
 */
async function resolveAuditorProfiles(
  auditorIds: string[],
): Promise<
  Record<
    string,
    { id: string; full_name: string | null; email: string; avatar_url: string | null }
  >
> {
  const uniqueIds = [...new Set(auditorIds.filter(Boolean))];
  if (uniqueIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('profiles')
    .select('id, full_name, email, avatar_url')
    .in('id', uniqueIds);

  if (error) throw error;

  const map: Record<
    string,
    { id: string; full_name: string | null; email: string; avatar_url: string | null }
  > = {};
  for (const p of data ?? []) {
    map[p.id] = p as {
      id: string;
      full_name: string | null;
      email: string;
      avatar_url: string | null;
    };
  }
  return map;
}

/**
 * Resolves schedule info (name, frequency) for a set of schedule IDs.
 */
async function resolveScheduleInfo(
  scheduleIds: string[],
): Promise<
  Record<string, { id: string; name: string; frequency: LpaScheduleFrequency }>
> {
  const uniqueIds = [...new Set(scheduleIds.filter(Boolean))];
  if (uniqueIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedules')
    .select('id, name, frequency')
    .in('id', uniqueIds);

  if (error) throw error;

  const map: Record<
    string,
    { id: string; name: string; frequency: LpaScheduleFrequency }
  > = {};
  for (const s of data ?? []) {
    map[s.id] = s as { id: string; name: string; frequency: LpaScheduleFrequency };
  }
  return map;
}

/**
 * Resolves aggregate stats for schedules used in execution cards/lists.
 * - question_count: active questions on the schedule
 * - estimated_duration_seconds: sum of active question durations
 */
async function resolveScheduleStats(
  scheduleIds: string[],
): Promise<Record<string, { question_count: number; estimated_duration_seconds: number }>> {
  const uniqueIds = [...new Set(scheduleIds.filter(Boolean))];
  if (uniqueIds.length === 0) return {};

  const { data: assignments, error: assignmentError } = await supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('schedule_id, question_id, is_active')
    .in('schedule_id', uniqueIds);

  if (assignmentError) throw assignmentError;

  const activeAssignments = (assignments ?? []).filter((a) => a.is_active);
  const questionIds = [...new Set(activeAssignments.map((a) => a.question_id))];

  let durationMap = new Map<string, number>();
  if (questionIds.length > 0) {
    const { data: questions, error: questionError } = await supabase
      .schema('core')
      .from('mod_lpa_questions')
      .select('id, estimated_duration_seconds')
      .in('id', questionIds);

    if (questionError) throw questionError;

    durationMap = new Map(
      (questions ?? []).map((q) => [q.id, Math.max(0, q.estimated_duration_seconds ?? 0)]),
    );
  }

  const stats: Record<string, { question_count: number; estimated_duration_seconds: number }> = {};
  for (const scheduleId of uniqueIds) {
    stats[scheduleId] = { question_count: 0, estimated_duration_seconds: 0 };
  }

  for (const row of activeAssignments) {
    const scheduleStats = stats[row.schedule_id] ?? {
      question_count: 0,
      estimated_duration_seconds: 0,
    };
    scheduleStats.question_count += 1;
    scheduleStats.estimated_duration_seconds += durationMap.get(row.question_id) ?? 0;
    stats[row.schedule_id] = scheduleStats;
  }

  return stats;
}

function buildLocationLabel(parts: {
  area_name?: string | null;
  line_name?: string | null;
  station_name?: string | null;
}): string {
  const labels = [parts.area_name, parts.line_name, parts.station_name].filter(Boolean);
  if (labels.length === 0) return 'Entire factory';
  return labels.join(' > ');
}

/**
 * Resolves audit info (name + location) for a set of audit IDs.
 */
async function resolveAuditInfo(
  auditIds: string[],
): Promise<
  Record<
    string,
    {
      id: string;
      name: string;
      area_name: string | null;
      line_name: string | null;
      station_name: string | null;
      location_label: string;
    }
  >
> {
  const uniqueIds = [...new Set(auditIds.filter(Boolean))];
  if (uniqueIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_audits')
    .select('id, name, area_id, line_id, station_id')
    .in('id', uniqueIds);

  if (error) throw error;

  const audits = (data ?? []) as Array<{
    id: string;
    name: string;
    area_id: string | null;
    line_id: string | null;
    station_id: string | null;
  }>;
  const areaIds = [...new Set(audits.map((a) => a.area_id).filter(Boolean))] as string[];
  const lineIds = [...new Set(audits.map((a) => a.line_id).filter(Boolean))] as string[];
  const stationIds = [...new Set(audits.map((a) => a.station_id).filter(Boolean))] as string[];

  const [areas, lines, stations] = await Promise.all([
    areaIds.length > 0
      ? supabase
          .schema('core')
          .from('factory_areas')
          .select('id, name')
          .in('id', areaIds)
          .then(({ data: rows, error: rowError }) => {
            if (rowError) throw rowError;
            return rows ?? [];
          })
      : Promise.resolve([]),
    lineIds.length > 0
      ? supabase
          .schema('core')
          .from('production_lines')
          .select('id, name')
          .in('id', lineIds)
          .then(({ data: rows, error: rowError }) => {
            if (rowError) throw rowError;
            return rows ?? [];
          })
      : Promise.resolve([]),
    stationIds.length > 0
      ? supabase
          .schema('core')
          .from('stations')
          .select('id, name')
          .in('id', stationIds)
          .then(({ data: rows, error: rowError }) => {
            if (rowError) throw rowError;
            return rows ?? [];
          })
      : Promise.resolve([]),
  ]);

  const areaMap: Record<string, string> = {};
  for (const row of areas as Array<{ id: string; name: string }>) {
    areaMap[row.id] = row.name;
  }

  const lineMap: Record<string, string> = {};
  for (const row of lines as Array<{ id: string; name: string }>) {
    lineMap[row.id] = row.name;
  }

  const stationMap: Record<string, string> = {};
  for (const row of stations as Array<{ id: string; name: string }>) {
    stationMap[row.id] = row.name;
  }

  const map: Record<
    string,
    {
      id: string;
      name: string;
      area_name: string | null;
      line_name: string | null;
      station_name: string | null;
      location_label: string;
    }
  > = {};

  for (const a of audits) {
    const areaName = a.area_id ? (areaMap[a.area_id] ?? null) : null;
    const lineName = a.line_id ? (lineMap[a.line_id] ?? null) : null;
    const stationName = a.station_id ? (stationMap[a.station_id] ?? null) : null;

    map[a.id] = {
      id: a.id,
      name: a.name,
      area_name: areaName,
      line_name: lineName,
      station_name: stationName,
      location_label: buildLocationLabel({
        area_name: areaName,
        line_name: lineName,
        station_name: stationName,
      }),
    };
  }

  return map;
}

/**
 * Resolves schedule-question execution snapshot for a schedule.
 */
async function resolveScheduleExecutionQuestions(
  scheduleId: string,
  tenantId?: string,
): Promise<LpaScheduleQuestionExecution[]> {
  let assignmentQuery = supabase
    .schema('core')
    .from('mod_lpa_schedule_questions')
    .select('id, question_id, sort_order, is_active')
    .eq('schedule_id', scheduleId)
    .order('sort_order', { ascending: true });

  if (tenantId) {
    assignmentQuery = assignmentQuery.eq('tenant_id', tenantId);
  }

  const { data: assignments, error: assignmentError } = await assignmentQuery;
  if (assignmentError) throw assignmentError;

  const rows = assignments ?? [];
  if (rows.length === 0) return [];

  const questionIds = [...new Set(rows.map((r) => r.question_id))];

  let questionQuery = supabase
    .schema('core')
    .from('mod_lpa_questions')
    .select(
      'id, question, question_translations, explanation, explanation_translations, reaction_plan, reaction_plan_translations, estimated_duration_seconds, is_active',
    )
    .in('id', questionIds);

  if (tenantId) {
    questionQuery = questionQuery.eq('tenant_id', tenantId);
  }

  const { data: questions, error: questionError } = await questionQuery;
  if (questionError) throw questionError;

  const questionMap = new Map((questions ?? []).map((q) => [q.id, q]));

  return rows.map((row) => {
    const question = questionMap.get(row.question_id);

    return {
      schedule_question_id: row.id,
      question_id: row.question_id,
      sort_order: row.sort_order,
      is_active: row.is_active,
      question: question
        ? {
            id: question.id,
            question: question.question,
            question_translations: question.question_translations as Record<string, string>,
            explanation: question.explanation,
            explanation_translations:
              (question.explanation_translations as Record<string, string>) ?? {},
            reaction_plan: question.reaction_plan,
            reaction_plan_translations:
              (question.reaction_plan_translations as Record<string, string>) ?? {},
            estimated_duration_seconds: question.estimated_duration_seconds,
            is_active: question.is_active,
          }
        : {
            id: row.question_id,
            question: '[Deleted question]',
            question_translations: {},
            explanation: null,
            explanation_translations: {},
            reaction_plan: null,
            reaction_plan_translations: {},
            estimated_duration_seconds: null,
            is_active: false,
          },
    };
  });
}

/**
 * Enriches raw instances with schedule, auditor, and audit details.
 */
async function enrichInstances(
  instances: LpaScheduleInstance[],
): Promise<LpaScheduleInstanceWithDetails[]> {
  if (instances.length === 0) return [];

  const [auditorMap, scheduleMap, auditMap, scheduleStats] = await Promise.all([
    resolveAuditorProfiles(instances.map((i) => i.auditor_id)),
    resolveScheduleInfo(instances.map((i) => i.schedule_id)),
    resolveAuditInfo(instances.map((i) => i.audit_id)),
    resolveScheduleStats(instances.map((i) => i.schedule_id)),
  ]);

  return instances.map((i) => ({
    ...i,
    auditor: auditorMap[i.auditor_id] ?? {
      id: i.auditor_id,
      full_name: null,
      email: 'Unknown',
      avatar_url: null,
    },
    schedule: {
      ...(scheduleMap[i.schedule_id] ?? {
        id: i.schedule_id,
        name: '[Deleted schedule]',
        frequency: 'weekly' as LpaScheduleFrequency,
      }),
      question_count: scheduleStats[i.schedule_id]?.question_count ?? 0,
      estimated_duration_seconds:
        scheduleStats[i.schedule_id]?.estimated_duration_seconds ?? 0,
    },
    audit: auditMap[i.audit_id] ?? {
      id: i.audit_id,
      name: '[Deleted audit]',
      area_name: null,
      line_name: null,
      station_name: null,
      location_label: 'Entire factory',
    },
  }));
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetch all instances for an audit (across all its schedules).
 * Ordered by period_start ascending, then schedule name.
 * Optionally filtered by date range.
 */
export async function fetchInstancesByAuditId(
  auditId: string,
  dateRange?: { from?: string; to?: string },
  tenantId?: string,
): Promise<LpaScheduleInstanceWithDetails[]> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .select('*')
    .eq('audit_id', auditId)
    .order('period_start', { ascending: true })
    .order('created_at', { ascending: true });

  if (dateRange?.from) {
    query = query.gte('period_start', dateRange.from);
  }
  if (dateRange?.to) {
    query = query.lte('period_end', dateRange.to);
  }
  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const instances = (data ?? []) as unknown as LpaScheduleInstance[];
  return enrichInstances(instances);
}

/**
 * Fetch all instances for a specific schedule.
 * Ordered by period_start ascending.
 */
export async function fetchInstancesByScheduleId(
  scheduleId: string,
  tenantId?: string,
): Promise<LpaScheduleInstanceWithDetails[]> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .select('*')
    .eq('schedule_id', scheduleId)
    .order('period_start', { ascending: true });

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;

  if (error) throw error;

  const instances = (data ?? []) as unknown as LpaScheduleInstance[];
  return enrichInstances(instances);
}

/**
 * Fetch instances assigned to a specific auditor (for "My Audits" in Phase 4).
 * Can filter by status and date range.
 */
export async function fetchInstancesByAuditorId(
  auditorId: string,
  filters?: { statuses?: string[]; from?: string; to?: string },
  tenantId?: string,
): Promise<LpaScheduleInstanceWithDetails[]> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .select('*')
    .eq('auditor_id', auditorId)
    .order('due_date', { ascending: true });

  if (filters?.statuses && filters.statuses.length > 0) {
    query = query.in('status', filters.statuses);
  }
  if (filters?.from) {
    query = query.gte('period_start', filters.from);
  }
  if (filters?.to) {
    query = query.lte('period_end', filters.to);
  }
  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const instances = (data ?? []) as unknown as LpaScheduleInstance[];
  return enrichInstances(instances);
}

/**
 * Fetch a single instance by ID with full details.
 */
export async function fetchInstanceById(
  instanceId: string,
  tenantId?: string,
): Promise<LpaScheduleInstanceWithDetails | null> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .select('*')
    .eq('id', instanceId);

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query.maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const instance = data as unknown as LpaScheduleInstance;
  const [enriched] = await enrichInstances([instance]);

  if (!enriched) return null;

  const questions = await resolveScheduleExecutionQuestions(
    enriched.schedule.id,
    tenantId,
  );

  return {
    ...enriched,
    questions,
  };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Generate (create) a single instance for a schedule.
 * This is the manual "tedious" generation for testing purposes.
 * The instance is immediately frozen (frozen_at = now()).
 *
 * The unique constraint (schedule_id, auditor_id, period_start) prevents
 * duplicate generation — if an instance already exists for this period,
 * Supabase will return a conflict error.
 */
export async function generateInstance(
  input: GenerateInstanceInput,
): Promise<LpaScheduleInstance> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .insert({
      tenant_id: input.tenant_id,
      schedule_id: input.schedule_id,
      audit_id: input.audit_id,
      auditor_id: input.auditor_id,
      period_start: input.period_start,
      period_end: input.period_end,
      due_date: input.due_date,
      status: 'pending',
      frozen_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as LpaScheduleInstance;
}

/**
 * Update the status of an instance.
 * Used by supervisors for manual skip/cancel actions.
 */
export async function updateInstanceStatus(
  instanceId: string,
  input: UpdateInstanceStatusInput,
  tenantId?: string,
): Promise<LpaScheduleInstance> {
  const updatePayload: Record<string, unknown> = {
    status: input.status,
  };

  // Auto-set completed_at when marking as completed
  if (input.status === 'completed') {
    updatePayload.completed_at = new Date().toISOString();
  }

  // Clear completed_at when reverting from completed
  if (input.status === 'pending' || input.status === 'cancelled') {
    updatePayload.completed_at = null;
  }

  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .update(updatePayload)
    .eq('id', instanceId);

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query.select().single();

  if (error) throw error;
  return data as unknown as LpaScheduleInstance;
}

/**
 * Reassign an instance to a different auditor (vacation substitution).
 *
 * Important: The unique constraint is (schedule_id, auditor_id, period_start).
 * Changing the auditor effectively creates a "substitution" entry.
 * If there's already an instance for the new auditor + same period + same schedule,
 * this will fail with a conflict — which is correct behavior.
 */
export async function updateInstanceAuditor(
  instanceId: string,
  input: UpdateInstanceAuditorInput,
  tenantId?: string,
): Promise<LpaScheduleInstance> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .update({ auditor_id: input.auditor_id })
    .eq('id', instanceId);

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { data, error } = await query.select().single();

  if (error) throw error;
  return data as unknown as LpaScheduleInstance;
}

/**
 * Delete an instance. Only allowed for pending instances that haven't been started.
 * The caller should check preconditions; the DB won't prevent deletion of
 * any status (unlike schedules with instances).
 */
export async function deleteInstance(
  instanceId: string,
  tenantId?: string,
): Promise<void> {
  let query = supabase
    .schema('core')
    .from('mod_lpa_schedule_instances')
    .delete()
    .eq('id', instanceId);

  if (tenantId) {
    query = query.eq('tenant_id', tenantId);
  }

  const { error } = await query;

  if (error) throw error;
}
